import { SectionCard } from 'app/components/SectionCard'
import { formatDate } from 'app/date.utils'
import { type SmsAgentDeployment, deploymentState, type SmsAgentTool } from './smsAgentDeployment'

/** One "label / value" pair in the metadata strip. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-body-xs text-subtle">{label}</span>
      <span className="truncate text-body-sm font-medium text-strong">{value}</span>
    </div>
  )
}

function ParamList({ label, names }: { label: string; names: string[] }) {
  if (names.length === 0) return null
  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-1">
      <span className="text-body-xs text-subtle">{label}</span>
      {names.map((name) => (
        <code key={name} className="rounded bg-surface-muted px-1 py-px font-mono text-[11px] text-default">
          {name}
        </code>
      ))}
    </div>
  )
}

function ToolRow({ tool }: { tool: SmsAgentTool }) {
  return (
    <li className="flex flex-col gap-1.5 rounded-md border border-default bg-surface px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <code className="font-mono text-body-sm font-medium text-strong">{tool.name}</code>
        <span className="rounded bg-surface-muted px-1.5 py-px font-mono text-[11px] text-subtle">{tool.method}</span>
        {/* Never the credential itself, only that the call carries one. See smsAgentDeployment.ts. */}
        {tool.authenticated && (
          <span className="rounded bg-surface-muted px-1.5 py-px text-[11px] text-subtle">authenticated</span>
        )}
      </div>
      {tool.description && <p className="text-body-xs text-subtle">{tool.description}</p>}
      <p className="truncate font-mono text-[11px] text-subtle" title={tool.url}>
        {tool.url}
      </p>
      <div className="flex flex-col gap-1">
        <ParamList label="Path" names={tool.params.path} />
        <ParamList label="Query" names={tool.params.query} />
        <ParamList label="Body" names={tool.params.body} />
      </div>
    </li>
  )
}

/**
 * What this location's SMS agent is actually running: the deployed prompt, the model behind it, and
 * the tools it can call. Read only. Atlas compiles and deploys the prompt; Trackable shows it.
 *
 * The page exists because none of this was visible from the product. A location silently moved from
 * gpt-4o to gpt-5.1 with the prompt going 21k to 90k characters, and answering "does this agent even
 * have a prompt" meant querying the ops schema by hand.
 */
export function SmsAgentSection({
  deployment,
  fetching,
  error,
}: {
  deployment?: SmsAgentDeployment
  fetching?: boolean
  error?: boolean
}) {
  const state = deploymentState(deployment)

  return (
    <SectionCard title="SMS agent">
      {error ? (
        <p className="text-body-sm text-subtle">The deployed prompt could not be loaded.</p>
      ) : fetching && !deployment ? (
        <p className="text-body-sm text-subtle">Loading…</p>
      ) : state === `never` ? (
        <p className="text-body-sm text-subtle">
          No prompt has been deployed to this location. The agent will not open or answer a conversation here until one
          is.
        </p>
      ) : (
        <>
          {state === `empty` && (
            <p className="text-body-sm text-subtle">
              The prompt deployed to this location is empty, so the agent will not open or answer a conversation here.
            </p>
          )}

          <div className="flex flex-wrap gap-x-10 gap-y-3">
            <Fact label="Deployed" value={formatDate({ value: deployment?.deployedDate }) || `Unknown`} />
            <Fact label="Version" value={deployment?.promptVersion ?? `Not versioned`} />
            <Fact label="Model" value={deployment?.model ?? `Not set`} />
            <Fact
              label="Temperature"
              value={deployment?.temperature != null ? String(deployment.temperature) : `Not set`}
            />
          </div>

          {state === `deployed` && (
            <div className="flex flex-col gap-2">
              <span className="text-body-xs text-subtle">
                Prompt · {deployment!.prompt.length.toLocaleString()} characters
              </span>
              {/* Its own scroll region: these run to 90,000 characters and must not push the page. */}
              <pre className="max-h-96 overflow-auto rounded-md border border-default bg-surface p-3 font-mono text-[11px] leading-relaxed whitespace-pre-wrap text-default">
                {deployment!.prompt}
              </pre>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-body-xs text-subtle">
              Tools{deployment && deployment.tools.length > 0 ? ` · ${deployment.tools.length}` : ``}
            </span>
            {deployment && deployment.tools.length > 0 ? (
              <ul className="flex flex-col gap-2">
                {deployment.tools.map((tool) => (
                  <ToolRow key={tool.name} tool={tool} />
                ))}
              </ul>
            ) : (
              <p className="text-body-sm text-subtle">
                No tools are configured, so the agent can talk but cannot look anything up or book.
              </p>
            )}
          </div>
        </>
      )}
    </SectionCard>
  )
}
